import { Card, CardFooter, FadeContent, FadeWrapper } from 'components/common';
import CustomScrollbar from 'components/common/CustomScrollbar';
import { CommonLink } from 'components/common/Forms/Button';
import { PatientLayout } from 'containers/PatientLayout';
import { useAuth } from 'context/AuthContext';
import { useCommonTranslation } from '../../../hooks/i18n/useCommonTranslation';
import { PageHelmet } from '../../../components/common/PageHelmet';
import { LegalPolicyTabs } from './LegalPolicyTabs';

export const TermsAndConditionsPage = () => {
  const { sangixUser } = useAuth();
  const { t } = useCommonTranslation();

  return (
    <PatientLayout graphics={Boolean(sangixUser) ? 'patient' : 'unauth'}>
      <PageHelmet title={t('terms-and-conditions')} />
      <Card fade>
        <FadeWrapper>
          <CustomScrollbar
            style={{ width: '100%' }}
            autoHeight
            autoHeightMin={'100%'}
            autoHeightMax={'100%'}
          >
            <FadeContent>
              <LegalPolicyTabs />
              <h2>Sangix Appointments — Text and RCS Messaging Terms</h2>
              <p>For patients receiving appointment messages</p>

              <h3>1. About these terms</h3>
              <p>
                These terms explain the text and RCS messages you may receive
                from Sangix Appointments about your appointments, and how you
                can stop them.
              </p>
              <p>
                Sangix Appointments is an appointment booking and messaging
                service operated by PORTALS UK Limited (trading as
                &quot;Sangix&quot;), a company registered in England and Wales
                (company number 06516839), registered office 66 Paul Street,
                London EC2A 4NA. We provide this service to NHS and healthcare
                organisations (&quot;your healthcare provider&quot;).
              </p>
              <p>
                Your healthcare provider decides what messages are sent to you
                and is responsible for your personal data (the &quot;data
                controller&quot;). Sangix sends the messages on their behalf
                (the &quot;data processor&quot;). If you have questions about
                your appointments or your data, please contact your healthcare
                provider directly. See section 11 for more on privacy.
              </p>

              <h3>2. What this service is</h3>
              <p>
                If your healthcare provider uses Sangix Appointments, you may
                receive automated messages about your appointments, such as:
              </p>
              <ul>
                <li>appointment confirmations;</li>
                <li>appointment reminders;</li>
                <li>
                  notifications about changes, rescheduling or cancellations of
                  your appointments.
                </li>
              </ul>
              <p>
                These are service messages about the care you have arranged.
                They are not marketing. We will not send you advertising, offers
                or promotions through this service.
              </p>
              <p>
                Messages are delivered by RCS (Rich Communication Services)
                where your phone and network support it, and otherwise by SMS
                (standard text message). You do not need to do anything to
                choose between them — if RCS is not available, the message is
                sent as an SMS instead.
              </p>

              <h3>3. How you receive these messages</h3>
              <p>
                You receive these messages because you gave your mobile number
                to your healthcare provider so they could contact you about your
                appointments. Because these are messages about your own care,
                they are sent to you as part of that service.
              </p>

              <h3>4. Message frequency</h3>
              <p>
                Message frequency varies and depends on your appointments — for
                example, when you book, when an appointment is coming up, or
                when something changes.
              </p>

              <h3>5. Cost</h3>
              <p>
                Sangix and your healthcare provider do not charge you to receive
                these messages. However, message and data rates may apply
                depending on your own mobile phone plan and provider.
              </p>

              <h3>6. Stopping messages — reply STOP</h3>
              <p>
                You can stop these messages at any time: reply STOP to any
                message. We will send one confirmation that you have been
                unsubscribed, and you will not receive further appointment
                messages.
              </p>
              <p>
                Stopping messages does not cancel your appointments. Any
                appointments you have already booked remain booked. You can
                still view and manage your appointments through the Sangix
                online booking system, or by contacting your healthcare provider
                or clinic directly. Please note that if you opt out you may miss
                helpful reminders about upcoming appointments.
              </p>
              <p>
                If you change your mind, reply START to begin receiving messages
                again, or ask your healthcare provider to re-enable them.
              </p>

              <h3>7. Getting help</h3>
              <p>
                If you need help with these messages, please contact us at
                support@sangix.co.uk. For anything about your appointments,
                please contact your healthcare provider or clinic.
              </p>

              <h3>8. Delivery and networks</h3>
              <p>
                Messages depend on your mobile network and device. Delivery is
                not guaranteed and messages may be delayed or, occasionally, not
                delivered — mobile carriers are not liable for delayed or
                undelivered messages. RCS messages require a compatible device
                and network; where these are not available, the message is sent
                as an SMS.
              </p>

              <h3>9. Keeping your messages secure</h3>
              <p>
                Messages may contain limited appointment details (such as a
                date, time or clinic). Please keep your phone secure and be
                aware of who can see your messages. If your mobile number
                changes, or if you receive a message that does not appear to be
                meant for you, please tell your healthcare provider so they can
                update their records.
              </p>

              <h3>10. Not for emergencies or clinical advice</h3>
              <p>
                These messages are automated and are for appointment
                administration only. Do not rely on replies to this service for
                urgent, medical or clinical matters, and do not use them to seek
                clinical advice. If you need medical help, contact your clinic,
                call NHS 111, or in an emergency call 999.
              </p>

              <h3>11. Your privacy</h3>
              <p>
                Your healthcare provider is the data controller for your
                personal data and decides how it is used. Sangix (PORTALS UK
                Limited) acts as the data processor, sending these messages on
                your healthcare provider&apos;s instructions under a written
                data processing agreement.
              </p>
              <p>
                We process your personal data to deliver appointment messages.
                Because this relates to your healthcare, the lawful bases relied
                on are typically UK GDPR Article 6(1) (public task or contract,
                as determined by your healthcare provider) together with Article
                9(2)(h) (provision of health or social care). These are service
                communications about your care and are not processed for direct
                marketing.
              </p>
              <p>
                Your data is handled on secure infrastructure and shared only
                with the providers needed to deliver the service — including our
                cloud hosting provider and our messaging provider, Vonage, which
                transmits the RCS and SMS messages on our behalf. Details of how
                your data is used, who it is shared with, how long it is kept,
                and your rights (including the right to object) are set out in
                our privacy notice: [Privacy Notice URL]. For requests relating
                to your personal data, please contact your healthcare provider
                (the data controller), or email us at support@sangix.co.uk. You
                also have the right to complain to the Information
                Commissioner&apos;s Office (ICO) at ico.org.uk.
              </p>

              <h3>12. Changes to these terms</h3>
              <p>
                We may update these terms from time to time — for example, to
                reflect changes to the service or to legal requirements. The
                current version is always available at this page, with the
                effective date shown at the top.
              </p>

              <h3>13. Who we are and how to contact us</h3>
              <p>PORTALS UK Limited, trading as Sangix Appointments</p>
              <p>Registered in England and Wales, company number 06516839</p>
              <p>Registered office: 66 Paul Street, London EC2A 4NA</p>
              <p>Service support: support@sangix.co.uk</p>
              <p>
                For questions about your appointments or your personal data,
                please contact your healthcare provider.
              </p>

              <h3>14. Governing law</h3>
              <p>These terms are governed by the laws of England and Wales.</p>

              <p>
                <em>
                  Quick reference: Message frequency varies. Message and data
                  rates may apply. Reply STOP to unsubscribe.
                </em>
              </p>
            </FadeContent>
          </CustomScrollbar>
        </FadeWrapper>
        <CardFooter>
          <CommonLink to="/" variant="secondary">
            {t('back-to-menu')}
          </CommonLink>
        </CardFooter>
      </Card>
    </PatientLayout>
  );
};
